import React, { useState, useRef, useEffect } from 'react'
import { Check, X, Loader2 } from 'lucide-react'

interface EditableTextProps {
  value: string
  onSave: (value: string) => Promise<void>
  maxLength?: number
  placeholder?: string
  className?: string
  multiline?: boolean
}

export default function EditableText({ 
  value, 
  onSave, 
  maxLength, 
  placeholder = "Klicken zum Bearbeiten",
  className = "",
  multiline = false
}: EditableTextProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(value)
  const [isSaving, setIsSaving] = useState(false)
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null)

  useEffect(() => {
    setEditValue(value)
  }, [value])

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  const handleSave = async () => {
    if (editValue.trim() === value.trim()) {
      setIsEditing(false)
      return
    }

    try {
      setIsSaving(true)
      await onSave(editValue.trim())
      setIsEditing(false)
    } catch (error) {
      console.error('Error saving:', error)
      // Reset to original value on error
      setEditValue(value)
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    setEditValue(value)
    setIsEditing(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !multiline) {
      e.preventDefault()
      handleSave()
    } else if (e.key === 'Enter' && multiline && e.ctrlKey) {
      e.preventDefault()
      handleSave()
    } else if (e.key === 'Escape') {
      handleCancel()
    }
  }

  if (isEditing) {
    const InputComponent = multiline ? 'textarea' : 'input'
    const inputProps = {
      ref: inputRef as any,
      value: editValue,
      onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setEditValue(e.target.value),
      onKeyDown: handleKeyDown,
      onBlur: multiline ? undefined : handleSave, // Don't save on blur for multiline to avoid accidental saves
      maxLength,
      placeholder,
      className: `input-field w-full px-2 py-1 ${className}`,
      disabled: isSaving,
      ...(multiline && { rows: 3 })
    }

    return (
      <div className="relative">
        <InputComponent {...inputProps} />
        {multiline && (
          <div className="flex gap-2 mt-2">
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="px-3 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-1"
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Speichere...
                </>
              ) : (
                <>
                  <Check className="h-3 w-3" />
                  Speichern
                </>
              )}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              disabled={isSaving}
              className="px-3 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed flex items-center gap-1 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 dark:disabled:bg-gray-800"
            >
              <X className="h-3 w-3" />
              Abbrechen
            </button>
          </div>
        )}
        {!multiline && isSaving && (
          <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
            <Loader2 className="h-4 w-4 animate-spin text-indigo-600" />
          </div>
        )}
      </div>
    )
  }

  return (
    <div 
      className={`cursor-pointer rounded border border-transparent bg-white/70 px-2 py-1 transition-colors hover:border-gray-200 hover:bg-white dark:bg-gray-950/50 dark:hover:border-gray-600 dark:hover:bg-gray-900/80 ${className}`}
      onClick={() => setIsEditing(true)}
      title="Klicken zum Bearbeiten"
    >
      {value || (
        <span className="text-gray-400 italic dark:text-gray-500">{placeholder}</span>
      )}
    </div>
  )
}
